import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import '@ant-design/v5-patch-for-react-19';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import React, { useEffect, useState } from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { useCommonStore } from './store/common';
import { ensureSDK, getFeishuUser } from './utils/feishu';

const isDev = process.env.NODE_ENV === 'development';

// Feishu Check Component
const FeishuCheck: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isFeishu, setIsFeishu] = useState<boolean>(true);
    const [checking, setChecking] = useState<boolean>(true);

    useEffect(() => {
        const check = async () => {
            if (isDev) {
                setChecking(false);
                return;
            }

            try {
                // Load SDK first
                await ensureSDK();
                // Check if window.h5sdk exists and is valid (rudimentary check)
                if (window.h5sdk) {
                    setIsFeishu(true);
                } else {
                    setIsFeishu(false);
                }
            } catch (error) {
                console.error('Failed to load Feishu SDK', error);
                setIsFeishu(false);
            } finally {
                setChecking(false);
            }
        };
        check();
    }, []);

    if (isDev) return <>{children}</>;

    if (checking) return null;

    if (!isFeishu) {
        return (
            <div
                className="w-full h-full grid place-content-center"
                style={{
                    display: 'grid',
                    placeContent: 'center',
                    height: '100vh',
                    width: '100vw',
                }}
            >
                <div
                    className="flex flex-col gap-1 items-center justify-center"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <img src="/icon.svg" alt="" />
                    <h3>请在飞书中打开</h3>
                    <p>此应用需要在飞书客户端中使用</p>
                </div>
            </div>
        );
    }

    return <>{children}</>;
};

export async function getInitialState(): Promise<{
    settings?: Partial<LayoutSettings>;
    currentUser?: API.CurrentUser;
}> {
    const currentUser = await getFeishuUser();

    // Initialize common dictionaries/products at app entry
    try {
        await useCommonStore.getState().initCommonData();
    } catch (_e) {
        // ignore init failures; app should still render
    }

    return {
        settings: defaultSettings as Partial<LayoutSettings>,
        currentUser,
    };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
    initialState,
    setInitialState,
}) => {
    return {
        actionsRender: () => [],
        // avatarProps: {
        //   title: <AvatarName />,
        //   render: (_, avatarChildren) => {
        //     return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
        //   },
        // },
        waterMarkProps: {},
        bgLayoutImgList: [
            {
                src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
                left: 85,
                bottom: 100,
                height: '303px',
            },
            {
                src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
                bottom: -68,
                right: -45,
                height: '303px',
            },
            {
                src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
                bottom: 0,
                left: 0,
                width: '331px',
                height: '331px',
            },
        ],
        menuHeaderRender: undefined,
        // 自定义 403 页面
        // unAccessible: <div>unAccessible</div>,
        // 增加一个 loading 的状态
        childrenRender: (children) => {
            // if (initialState?.loading) return <PageLoading />;
            return (
                <>
                    {children}
                    {isDev && (
                        <SettingDrawer
                            disableUrlParams
                            enableDarkTheme
                            settings={initialState?.settings}
                            onSettingChange={(settings) => {
                                setInitialState((preInitialState) => ({
                                    ...preInitialState,
                                    settings,
                                }));
                            }}
                        />
                    )}
                </>
            );
        },
        ...initialState?.settings,
    };
};

export const request: RequestConfig = {
    baseURL: process.env.UMI_APP_BASE_URL || (isDev ? '/api' : '/celestin-shop'),
    ...errorConfig,
    requestInterceptors: [
        (url, options) => {
            // @ts-expect-error
            if (options.skipToken) {
                return { url, options };
            }

            const localToken = localStorage.getItem('token');
            // TODO: remove this hardcode token after dev
            const token = localToken;
            if (token) {
                const headers = {
                    ...options.headers,
                    Authorization: token,
                };
                return { url, options: { ...options, headers } };
            }
            return { url, options };
        },
    ],
};

export function rootContainer(container: React.ReactNode) {
    return <FeishuCheck>{container}</FeishuCheck>;
}
